import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Edit2, Trash2, Package, GripVertical, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";

// Forhåndsvisning af markør-symboler — bruges i stamdata + dropdown
export function MarkerSymbolPreview({ symbol, color, size = 22 }) {
  const c = color || '#a855f7';
  if (symbol === 'dot') {
    // Sort prik med farvet ring (bruger farve som ring så den er synlig på alle baggrunde)
    const dot = Math.round(size * 0.55);
    return (
      <div style={{ width: size, height: size, position: 'relative', display: 'inline-block' }}>
        <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: `2px solid ${c}`, opacity: 0.4 }} />
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: dot, height: dot, background: c, borderRadius: '50%' }} />
      </div>
    );
  }
  if (symbol === 'triangle') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" style={{ display: 'inline-block' }}>
        <polygon points="12,3 22,21 2,21" fill="none" stroke={c} strokeWidth="2.5" strokeLinejoin="round" />
      </svg>
    );
  }
  // circle (default)
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      border: `3px solid ${c}`, display: 'inline-block', boxSizing: 'border-box'
    }} />
  );
}

const SYMBOL_OPTIONS = [
  { value: 'dot', label: 'Prik' },
  { value: 'circle', label: 'Cirkel' },
  { value: 'triangle', label: 'Trekant' },
];

function DraggableList({ items, listId, onReorder, renderItem }) {
  const dragIdx = useRef(null);
  const overIdx = useRef(null);
  const [dragging, setDragging] = useState(null);
  const [over, setOver] = useState(null);

  const handleDragStart = (e, idx) => {
    dragIdx.current = idx;
    setDragging(idx);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", `${listId}-${idx}`);
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (overIdx.current !== idx) {
      overIdx.current = idx;
      setOver(idx);
    }
  };

  const handleDrop = (e, idx) => {
    e.preventDefault();
    const data = e.dataTransfer.getData("text/plain");
    if (!data.startsWith(listId)) return; // Ignorer drag fra anden liste

    const fromIdx = dragIdx.current;
    const toIdx = idx;

    if (fromIdx !== null && toIdx !== null && fromIdx !== toIdx) {
      const reordered = [...items];
      const [moved] = reordered.splice(fromIdx, 1);
      reordered.splice(toIdx, 0, moved);
      onReorder(reordered);
    }

    dragIdx.current = null;
    overIdx.current = null;
    setDragging(null);
    setOver(null);
  };

  const handleDragEnd = () => {
    dragIdx.current = null;
    overIdx.current = null;
    setDragging(null);
    setOver(null);
  };

  return (
    <div className="space-y-1">
      {items.map((item, idx) => (
        <div
          key={item.id}
          draggable
          onDragStart={(e) => handleDragStart(e, idx)}
          onDragOver={(e) => handleDragOver(e, idx)}
          onDrop={(e) => handleDrop(e, idx)}
          onDragEnd={handleDragEnd}
          className={`flex items-center justify-between p-2 bg-zinc-800/50 rounded transition-all
            ${dragging === idx ? 'opacity-30 scale-95' : ''}
            ${over === idx && dragging !== null && dragging !== idx ? 'border-t-2 border-[#5B8C2A] -mt-[2px]' : 'border-t-2 border-transparent'}
          `}
          style={{ cursor: 'grab' }}
          data-testid={`draggable-${listId}-${idx}`}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            {renderItem(item, idx)}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function RessourcerTab({ token, eventId, showConfirm, closeConfirm }) {
  const [fractionTypes, setFractionTypes] = useState([]);
  const [containerSizes, setContainerSizes] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [resourceConfigTab, setResourceConfigTab] = useState('ressourceø');
  const [showAddFraction, setShowAddFraction] = useState(false);
  const [editingFraction, setEditingFraction] = useState(null);
  const [showAddContainerSize, setShowAddContainerSize] = useState(false);
  const [editingContainerSize, setEditingContainerSize] = useState(null);
  const [showAddMarker, setShowAddMarker] = useState(false);
  const [editingMarker, setEditingMarker] = useState(null);
  const [markerSymbol, setMarkerSymbol] = useState('circle');
  const [markerColor, setMarkerColor] = useState('#a855f7');

  const loadFractionTypes = async () => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/resource-config/fractions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFractionTypes(res.data || []);
    } catch (error) {
      console.error("Fejl ved hentning af fraktionstyper", error);
    }
  };

  const loadContainerSizes = async () => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/resource-config/container-sizes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContainerSizes(res.data || []);
    } catch (error) {
      console.error("Fejl ved hentning af containerstørrelser", error);
    }
  };

  const loadMarkers = async () => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/resource-config/markers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMarkers(res.data || []);
    } catch (error) {
      console.error("Fejl ved hentning af markører", error);
    }
  };

  useEffect(() => {
    if (eventId) {
      loadFractionTypes();
      loadContainerSizes();
      loadMarkers();
    }
  }, [eventId]);

  const saveFractionType = async (data) => {
    try {
      if (editingFraction) {
        await axios.put(`${API}/events/${eventId}/resource-config/fractions/${editingFraction.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Fraktionstype opdateret");
      } else {
        await axios.post(`${API}/events/${eventId}/resource-config/fractions`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Fraktionstype oprettet");
      }
      setShowAddFraction(false);
      setEditingFraction(null);
      loadFractionTypes();
    } catch (error) {
      toast.error("Fejl ved gemning af fraktionstype");
    }
  };

  const deleteFractionType = (f) => {
    showConfirm(
      "Slet fraktionstype?",
      `Er du sikker på du vil slette "${f.name}"?`,
      async () => {
        try {
          await axios.delete(`${API}/events/${eventId}/resource-config/fractions/${f.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success("Fraktionstype slettet");
          loadFractionTypes();
        } catch (error) {
          toast.error("Fejl ved sletning");
        }
        closeConfirm();
      }
    );
  };

  const saveContainerSize = async (data) => {
    try {
      if (editingContainerSize) {
        await axios.put(`${API}/events/${eventId}/resource-config/container-sizes/${editingContainerSize.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Containerstørrelse opdateret");
      } else {
        await axios.post(`${API}/events/${eventId}/resource-config/container-sizes`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Containerstørrelse oprettet");
      }
      setShowAddContainerSize(false);
      setEditingContainerSize(null);
      loadContainerSizes();
    } catch (error) {
      toast.error("Fejl ved gemning af containerstørrelse");
    }
  };

  const deleteContainerSize = (cs) => {
    showConfirm(
      "Slet containerstørrelse?",
      `Er du sikker på du vil slette "${cs.name}"?`,
      async () => {
        try {
          await axios.delete(`${API}/events/${eventId}/resource-config/container-sizes/${cs.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success("Containerstørrelse slettet");
          loadContainerSizes();
        } catch (error) {
          toast.error("Fejl ved sletning");
        }
        closeConfirm();
      }
    );
  };

  const saveReorder = async (collection, reorderedItems) => {
    try {
      await axios.put(`${API}/events/${eventId}/resource-config/reorder`, {
        collection,
        items: reorderedItems.map((item, idx) => ({ id: item.id, sort_order: idx }))
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch {
      toast.error("Kunne ikke gemme rækkefølge");
    }
  };

  const saveMarker = async (data) => {
    try {
      if (editingMarker) {
        await axios.put(`${API}/events/${eventId}/resource-config/markers/${editingMarker.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Markør opdateret");
      } else {
        await axios.post(`${API}/events/${eventId}/resource-config/markers`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Markør oprettet");
      }
      setShowAddMarker(false);
      setEditingMarker(null);
      loadMarkers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved gemning af markør");
    }
  };

  const deleteMarker = (m) => {
    showConfirm(
      "Slet opmærksomhedsmarkør?",
      `Er du sikker på du vil slette "${m.name}"? Den fjernes fra alle punkter den er knyttet til.`,
      async () => {
        try {
          await axios.delete(`${API}/events/${eventId}/resource-config/markers/${m.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success("Markør slettet");
          loadMarkers();
        } catch (error) {
          toast.error("Fejl ved sletning");
        }
        closeConfirm();
      }
    );
  };

  const handleMarkerReorder = (reordered) => {
    const updated = reordered.map((item, idx) => ({ ...item, sort_order: idx }));
    setMarkers(updated);
    saveReorder('markers', updated);
  };

  const handleFractionReorder = (reordered) => {
    const others = fractionTypes.filter(f => f.point_type !== resourceConfigTab);
    const updated = reordered.map((item, idx) => ({ ...item, sort_order: idx }));
    setFractionTypes([...others, ...updated]);
    saveReorder('fractions', updated);
  };

  const handleContainerReorder = (reordered) => {
    const others = containerSizes.filter(cs => cs.point_type !== resourceConfigTab);
    const updated = reordered.map((item, idx) => ({ ...item, sort_order: idx }));
    setContainerSizes([...others, ...updated]);
    saveReorder('container_sizes', updated);
  };

  const filteredFractions = fractionTypes.filter(f => f.point_type === resourceConfigTab);
  const filteredContainers = containerSizes.filter(cs => cs.point_type === resourceConfigTab);
  const [applyingSortOrder, setApplyingSortOrder] = useState(false);

  const applySortOrderToAllPoints = async () => {
    setApplyingSortOrder(true);
    try {
      const res = await axios.put(`${API}/events/${eventId}/resource-config/apply-sort-order`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${res.data.updated} af ${res.data.total} punkter opdateret med ny rækkefølge`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved opdatering af punkter");
    } finally {
      setApplyingSortOrder(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex gap-2 border-b border-border pb-4">
        {['ressourceø', 'ressourcespand', 'maxicontainer'].map(pt => (
          <Button key={pt} variant={resourceConfigTab === pt ? "default" : "outline"} onClick={() => setResourceConfigTab(pt)} size="sm">
            {pt === 'ressourceø' ? 'Ressourceøer' : pt === 'ressourcespand' ? 'Ressourcespande' : 'Maxicontainere'}
          </Button>
        ))}
        <div className="ml-auto">
          <Button
            size="sm"
            variant="outline"
            onClick={applySortOrderToAllPoints}
            disabled={applyingSortOrder}
            className="text-xs"
            data-testid="apply-sort-order-btn"
          >
            <RefreshCw className={`h-3 w-3 mr-1 ${applyingSortOrder ? 'animate-spin' : ''}`} />
            {applyingSortOrder ? 'Opdaterer...' : 'Opdatér alle punkter'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold flex items-center gap-2"><Package className="h-4 w-4" />Fraktionstyper</h3>
            <Button size="sm" onClick={() => { setShowAddFraction(true); setEditingFraction(null); }} data-testid="add-fraction-btn"><Plus className="h-4 w-4" /></Button>
          </div>

          <DraggableList
            items={filteredFractions}
            listId={`fractions-${resourceConfigTab}`}
            onReorder={handleFractionReorder}
            renderItem={(f) => (
              <>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {f.color && <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: f.color }} />}
                  <span className="truncate">{f.name}</span>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setEditingFraction(f); setShowAddFraction(true); }}><Edit2 className="h-3 w-3" /></Button>
                  <Button size="sm" variant="ghost" className="text-red-400" onClick={(e) => { e.stopPropagation(); deleteFractionType(f); }}><Trash2 className="h-3 w-3" /></Button>
                </div>
              </>
            )}
          />

          {filteredFractions.length === 0 && (
            <p className="text-muted-foreground text-sm text-center py-4">Ingen fraktionstyper endnu</p>
          )}
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold flex items-center gap-2"><Package className="h-4 w-4" />Containerstørrelser</h3>
              <Button size="sm" onClick={() => { setShowAddContainerSize(true); setEditingContainerSize(null); }} data-testid="add-container-size-btn"><Plus className="h-4 w-4" /></Button>
            </div>

            <DraggableList
              items={filteredContainers}
              listId={`containers-${resourceConfigTab}`}
              onReorder={handleContainerReorder}
              renderItem={(cs) => (
                <>
                  <div className="flex-1 min-w-0">
                    <span className="truncate">{cs.name}</span>
                    {cs.liters && <span className="text-muted-foreground text-xs ml-2">({cs.liters}L)</span>}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setEditingContainerSize(cs); setShowAddContainerSize(true); }}><Edit2 className="h-3 w-3" /></Button>
                    <Button size="sm" variant="ghost" className="text-red-400" onClick={(e) => { e.stopPropagation(); deleteContainerSize(cs); }}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </>
              )}
            />

            {filteredContainers.length === 0 && (
              <p className="text-muted-foreground text-sm text-center py-4">Ingen containerstørrelser endnu</p>
            )}
          </div>
      </div>

      {/* OPMÆRKSOMHEDSMARKØRER — fælles for alle punkttyper */}
      <div className="bg-card border border-border rounded-lg p-4" data-testid="markers-section">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="font-bold flex items-center gap-2"><Sparkles className="h-4 w-4" />Opmærksomhedsmarkører</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Visuelle symboler du kan tilknytte til punkter (fx "Tømmes udenfor afvikling", "Dropzone"). De er ikke fraktioner og indgår ikke i tømnings-flowet.
            </p>
          </div>
          <Button size="sm" onClick={() => { setShowAddMarker(true); setEditingMarker(null); setMarkerSymbol('circle'); setMarkerColor('#a855f7'); }} data-testid="add-marker-btn">
            <Plus className="h-4 w-4 mr-1" /> Tilføj markør
          </Button>
        </div>

        <DraggableList
          items={markers}
          listId={`markers`}
          onReorder={handleMarkerReorder}
          renderItem={(m) => (
            <>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <MarkerSymbolPreview symbol={m.symbol} color={m.color} size={20} />
                <span className="truncate" data-testid={`marker-name-${m.id}`}>{m.name}</span>
                <span className="text-muted-foreground text-xs">
                  {SYMBOL_OPTIONS.find(s => s.value === m.symbol)?.label || m.symbol}
                </span>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <Button size="sm" variant="ghost" onClick={(e) => {
                  e.stopPropagation();
                  setEditingMarker(m);
                  setMarkerSymbol(m.symbol || 'circle');
                  setMarkerColor(m.color || '#a855f7');
                  setShowAddMarker(true);
                }} data-testid={`edit-marker-${m.id}`}><Edit2 className="h-3 w-3" /></Button>
                <Button size="sm" variant="ghost" className="text-red-400" onClick={(e) => { e.stopPropagation(); deleteMarker(m); }} data-testid={`delete-marker-${m.id}`}><Trash2 className="h-3 w-3" /></Button>
              </div>
            </>
          )}
        />

        {markers.length === 0 && (
          <p className="text-muted-foreground text-sm text-center py-4">Ingen markører endnu — tilføj fx "Tømmes udenfor afvikling" eller "Dropzone"</p>
        )}
      </div>

      {showAddFraction && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md">
            <h3 className="font-bold mb-4">{editingFraction ? 'Rediger' : 'Tilføj'} Fraktionstype</h3>
            <form onSubmit={(e) => { e.preventDefault(); const fd = new FormData(e.target); saveFractionType({ name: fd.get('name'), color: fd.get('color') || null, point_type: resourceConfigTab }); }}>
              <div className="space-y-4">
                <div><Label>Navn *</Label><Input name="name" defaultValue={editingFraction?.name || ''} required placeholder="F.eks. Rest, Pap, Plast" /></div>
                <div><Label>Farve (valgfri)</Label><Input name="color" type="color" defaultValue={editingFraction?.color || '#3b82f6'} /></div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <Button type="button" variant="outline" onClick={() => { setShowAddFraction(false); setEditingFraction(null); }}>Annuller</Button>
                <Button type="submit">Gem</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddContainerSize && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md">
            <h3 className="font-bold mb-4">{editingContainerSize ? 'Rediger' : 'Tilføj'} Containerstørrelse</h3>
            <form onSubmit={(e) => { e.preventDefault(); const fd = new FormData(e.target); saveContainerSize({ name: fd.get('name'), liters: fd.get('liters') ? parseInt(fd.get('liters')) : null, point_type: resourceConfigTab }); }}>
              <div className="space-y-4">
                <div><Label>Navn *</Label><Input name="name" defaultValue={editingContainerSize?.name || ''} required placeholder="F.eks. 240L, 660L" /></div>
                <div><Label>Liter (valgfri, til beregninger)</Label><Input name="liters" type="number" defaultValue={editingContainerSize?.liters || ''} placeholder="F.eks. 240" /></div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <Button type="button" variant="outline" onClick={() => { setShowAddContainerSize(false); setEditingContainerSize(null); }}>Annuller</Button>
                <Button type="submit">Gem</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddMarker && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" data-testid="marker-dialog">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md">
            <h3 className="font-bold mb-4">{editingMarker ? 'Rediger' : 'Tilføj'} Opmærksomhedsmarkør</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              const fd = new FormData(e.target);
              saveMarker({
                name: fd.get('name'),
                symbol: markerSymbol,
                color: markerColor,
              });
            }}>
              <div className="space-y-4">
                <div>
                  <Label>Navn *</Label>
                  <Input name="name" defaultValue={editingMarker?.name || ''} required placeholder="F.eks. Tømmes udenfor afvikling" data-testid="marker-name-input" />
                </div>
                <div>
                  <Label>Symbol *</Label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {SYMBOL_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setMarkerSymbol(opt.value)}
                        data-testid={`marker-symbol-${opt.value}`}
                        className={`flex flex-col items-center gap-2 p-3 rounded border-2 transition ${
                          markerSymbol === opt.value ? 'border-primary bg-primary/10' : 'border-border bg-zinc-800/30 hover:border-zinc-500'
                        }`}
                      >
                        <MarkerSymbolPreview symbol={opt.value} color={markerColor} size={28} />
                        <span className="text-xs">{opt.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Farve *</Label>
                  <div className="flex items-center gap-3 mt-2">
                    <Input
                      type="color"
                      value={markerColor}
                      onChange={(e) => setMarkerColor(e.target.value)}
                      className="w-20 h-10 p-1 cursor-pointer"
                      data-testid="marker-color-input"
                    />
                    <Input
                      type="text"
                      value={markerColor}
                      onChange={(e) => setMarkerColor(e.target.value)}
                      className="flex-1"
                      placeholder="#a855f7"
                    />
                    <MarkerSymbolPreview symbol={markerSymbol} color={markerColor} size={32} />
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <Button type="button" variant="outline" onClick={() => { setShowAddMarker(false); setEditingMarker(null); }}>Annuller</Button>
                <Button type="submit" data-testid="save-marker-btn">Gem</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
